import apiClient from '../client';

// Types
export interface SecurityDashboard {
  success: boolean;
  dashboard: {
    period_days: number;
    security_events: {
      total: number;
      critical: number;
      high: number;
      medium: number;
      low: number;
      unresolved: number;
    };
    threat_detection: {
      threats_detected: number;
      threats_blocked: number;
      false_positives: number;
      detection_rate: number;
    };
    authentication: {
      total_attempts: number;
      successful: number;
      failed: number;
      blocked: number;
      success_rate: number;
    };
    anomalies: {
      detected: number;
      investigated: number;
      confirmed: number;
      false_positives: number;
    };
    compliance_status: {
      score: number;
      status: string;
      last_assessment: string;
    };
  };
}

export interface SecurityEvent {
  id: number;
  event_id: string;
  event_type: string;
  severity: string;
  timestamp: string;
  source_ip: string;
  user_id?: string;
  resource_type?: string;
  resource_id?: string;
  description: string;
  threat_indicators: any;
  response_actions: any;
  resolved: boolean;
  resolved_at?: string;
  resolved_by?: string;
  false_positive: boolean;
}

export interface ThreatAlert {
  id: number;
  alert_id: string;
  threat_type: string;
  severity: string;
  confidence_score: number;
  detected_at: string;
  source_ip: string;
  target_resource: string;
  description: string;
  indicators: any;
  recommended_actions: any;
  status: string;
  investigated: boolean;
  blocked: boolean;
}

// API Functions
export const securityService = {
  // Get security dashboard
  getDashboard: async (days: number = 30): Promise<SecurityDashboard> => {
    const response = await apiClient.get(`/security/dashboard?days=${days}`);
    return response.data;
  },

  // Get security events
  getEvents: async (params?: {
    severity?: string;
    event_type?: string;
    resolved?: boolean;
    days?: number;
    skip?: number;
    limit?: number;
  }): Promise<{ events: SecurityEvent[]; total: number }> => {
    const response = await apiClient.get('/security/events', { params });
    return response.data;
  },

  // Get threat alerts
  getThreats: async (params?: {
    threat_type?: string;
    severity?: string;
    status?: string;
    days?: number;
    skip?: number;
    limit?: number;
  }): Promise<{ threats: ThreatAlert[]; total: number }> => {
    const response = await apiClient.get('/security/threats', { params });
    return response.data;
  },

  // Resolve security event
  resolveEvent: async (eventId: string, resolution: {
    resolution_notes: string;
    false_positive?: boolean;
    actions_taken?: any;
  }): Promise<{ success: boolean; event: SecurityEvent }> => {
    const response = await apiClient.post(`/security/events/${eventId}/resolve`, resolution);
    return response.data;
  },

  // Block threat
  blockThreat: async (alertId: string, blockData: {
    block_reason: string;
    block_duration_hours?: number;
    notify_admin?: boolean;
  }): Promise<{ success: boolean; alert: ThreatAlert }> => {
    const response = await apiClient.post(`/security/threats/${alertId}/block`, blockData);
    return response.data;
  },

  // Get authentication logs
  getAuthLogs: async (params?: {
    user_id?: string;
    success?: boolean;
    days?: number;
    skip?: number;
    limit?: number;
  }): Promise<{ logs: any[]; total: number }> => {
    const response = await apiClient.get('/security/auth-logs', { params });
    return response.data;
  },

  // Get anomalies
  getAnomalies: async (params?: {
    anomaly_type?: string;
    severity?: string;
    investigated?: boolean;
    days?: number;
    skip?: number;
    limit?: number;
  }): Promise<{ anomalies: any[]; total: number }> => {
    const response = await apiClient.get('/security/anomalies', { params });
    return response.data;
  },

  // Run security scan
  runScan: async (scanType: string): Promise<{ success: boolean; scan_id: string }> => {
    const response = await apiClient.post('/security/scan', { scan_type: scanType });
    return response.data;
  },

  // Get compliance status
  getComplianceStatus: async (): Promise<{
    success: boolean;
    compliance: {
      overall_score: number;
      status: string;
      frameworks: any[];
      last_assessment: string;
    };
  }> => {
    const response = await apiClient.get('/security/compliance-status');
    return response.data;
  },
};

export default securityService;

